/**
 * Atomic Design - Button Atom
 * Base button component with multiple variants and sizes
 */

<template>
  <button
    :class="['ds-button', `ds-button--${variant}`, `ds-button--${size}`, { 'ds-button--disabled': disabled || loading, 'ds-button--loading': loading }]"
    :disabled="disabled || loading"
    :aria-busy="loading ? 'true' : undefined"
    :type="type"
    @click="$emit('click', $event)"
  >
    <span v-if="loading" class="ds-button__spinner" data-testid="ds-button-spinner" aria-hidden="true" />
    <span class="ds-button__content">
      <slot />
    </span>
  </button>
</template>

<script setup>
defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (v) => ['primary', 'secondary', 'accent', 'ghost', 'danger'].includes(v),
  },
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  type: {
    type: String,
    default: 'button',
    validator: (v) => ['button', 'submit', 'reset'].includes(v),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /* Loading: shows a spinner and blocks clicks (disabled + aria-busy) so a
   * submit can't fire twice while in flight. / Carregando: spinner + bloqueia
   * clique (disabled + aria-busy) pra evitar duplo submit. */
  loading: {
    type: Boolean,
    default: false,
  },
});

defineEmits({ click: (event) => event instanceof Event });
</script>

<style scoped>
.ds-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  font-family: var(--font-family-base);
  font-weight: var(--weight-semibold, 600);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-default);
  white-space: nowrap;
  user-select: none;
  outline: none;
  position: relative;
}

/* Sizes */
.ds-button--sm {
  padding: 6px 12px;
  font-size: var(--font-size-body-small);
}

.ds-button--md {
  padding: 10px 16px;
  font-size: var(--font-size-body);
}

.ds-button--lg {
  padding: 12px 20px;
  font-size: var(--font-size-body);
}

/* Variants */
.ds-button--primary {
  /* SYN-2738 (A9): fundo vem do token do tema (--button-primary-bg); no
     synapsee-2026 é o vermelho escurecido (#D41920, 5.30:1 com branco). */
  background-color: var(--button-primary-bg, var(--color-primary));
  color: var(--color-text-inverse);
}

.ds-button--primary:hover:not(.ds-button--disabled) {
  background-color: var(--button-primary-bg-hover, var(--color-primary-dark));
  box-shadow: var(--shadow-md);
}

.ds-button--primary:active:not(.ds-button--disabled) {
  transform: scale(0.98);
}

.ds-button--secondary {
  background-color: var(--button-secondary-bg, var(--color-secondary));
  color: var(--color-text-inverse);
}

.ds-button--secondary:hover:not(.ds-button--disabled) {
  background-color: var(--button-secondary-bg-hover, var(--color-secondary-dark));
  box-shadow: var(--shadow-md);
}

.ds-button--secondary:active:not(.ds-button--disabled) {
  transform: scale(0.98);
}

.ds-button--accent {
  background-color: var(--color-accent);
  color: var(--button-accent-text, var(--color-text-primary));
}

.ds-button--accent:hover:not(.ds-button--disabled) {
  background-color: var(--color-accent-dark);
  box-shadow: var(--shadow-md);
}

.ds-button--accent:active:not(.ds-button--disabled) {
  transform: scale(0.98);
}

.ds-button--ghost {
  background-color: transparent;
  /* C12 (spec briefing-prioridades-ux, 2026-10-05): o texto vem do token do tema
     (--button-ghost-text). No synapsee-2026 é {colors.acao} #D41920 (5,30:1 sobre
     branco); o vermelho da marca cru (#ED1C24) dava 4,38:1 e reprovava AA em todo
     ghost de 14-16px ("Voltar", "Já conheço" — V5-1/V5-2). Os outros temas mantêm
     a cor primária. O fallback cobre quem renderiza sem tema aplicado (testes). */
  color: var(--button-ghost-text, var(--color-primary));
  border: 1px solid var(--color-border-default);
}

.ds-button--ghost:hover:not(.ds-button--disabled) {
  background-color: var(--color-surface-elevated);
  border-color: var(--color-primary);
}

.ds-button--ghost:active:not(.ds-button--disabled) {
  transform: scale(0.98);
}

.ds-button--danger {
  background-color: var(--color-error);
  color: var(--color-text-inverse);
}

.ds-button--danger:hover:not(.ds-button--disabled) {
  opacity: 0.9;
  box-shadow: var(--shadow-md);
}

.ds-button--danger:active:not(.ds-button--disabled) {
  transform: scale(0.98);
}

/* Disabled state */
.ds-button--disabled {
  opacity: var(--opacity-disabled, 0.5);
  cursor: not-allowed;
}

.ds-button__content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: inherit;
}

/* Loading state: spinner centered over the (invisible) label keeps the width stable */
.ds-button--loading {
  cursor: progress;
}

.ds-button--loading .ds-button__content {
  visibility: hidden;
}

.ds-button__spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 1em;
  height: 1em;
  margin: -0.5em 0 0 -0.5em;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ds-button-spin 0.7s linear infinite;
}

@keyframes ds-button-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ds-button__spinner {
    animation-duration: 2s;
  }
}

/* Focus state */
.ds-button:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}
</style>
